import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Sparkles } from 'lucide-react';
import { 
  getFeatureStats, 
  getTypeIcon, 
  getTypeColor, 
  getCategoryIcon, 
  getCategoryColor, 
  isFeatureNew 
} from '@/data/whatsNewFeatures';

interface FeatureCardsProps {
  /** Whether to show responsive mobile optimizations */
  mobile?: boolean;
  /** Custom animation delay multiplier */
  animationDelay?: number;
  /** Custom grid classes */
  gridClassName?: string;
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export const FeatureCards: React.FC<FeatureCardsProps> = ({ 
  mobile = false,
  animationDelay = 0.02,
  gridClassName = "grid gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3"
}) => {
  const { sortedFeatures } = getFeatureStats();

  return (
    <div className={gridClassName}>
      {sortedFeatures.map((feature, index) => {
        const TypeIcon = getTypeIcon(feature.type);
        const CategoryIcon = getCategoryIcon(feature.category);
        
        return (
          <motion.div
            key={feature.id}
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.5, delay: index * animationDelay }}
          >
            <Card className="h-full transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 hover:border-primary/30 group cursor-pointer">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className={`font-semibold leading-tight pr-2 ${
                    mobile ? 'text-base sm:text-lg' : 'text-lg'
                  }`}>
                    {feature.title}
                  </CardTitle>
                  {isFeatureNew(feature.date) && (
                    <Badge 
                      variant="secondary" 
                      className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 text-xs flex-shrink-0"
                    >
                      <Sparkles className="h-3 w-3 mr-1" />
                      New
                    </Badge>
                  )}
                </div>
                
                <div className={`flex flex-wrap items-center mt-2 ${
                  mobile ? 'gap-1.5 sm:gap-2' : 'gap-2'
                }`}>
                  <Badge variant="outline" className={`text-xs ${getTypeColor(feature.type)}`}>
                    <TypeIcon className="h-4 w-4" />
                    <span className={`ml-1 capitalize ${mobile ? 'hidden sm:inline' : ''}`}>
                      {feature.type}
                    </span>
                  </Badge>
                  <Badge variant="outline" className={`text-xs ${getCategoryColor(feature.category)}`}>
                    <CategoryIcon className="h-4 w-4" />
                    <span className={`ml-1 capitalize ${mobile ? 'hidden sm:inline' : ''}`}>
                      {feature.category}
                    </span>
                  </Badge>
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
                    <span className="truncate">
                      {mobile ? 
                        new Date(feature.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          year: typeof window !== 'undefined' && window.innerWidth > 640 ? 'numeric' : '2-digit'
                        }) :
                        new Date(feature.date).toLocaleDateString()
                      }
                    </span>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pb-4">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
};